import * as React from 'react';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { CalendarIcon, X } from 'lucide-react';
import { cn } from '@shared';
import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/Calendar';
import { Label } from '@/components/ui/Label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { parseLocalDate, formatLocalDate } from '@/utils/date';

export interface DaySheetlyDateRange {
  from: string | null;
  to: string | null;
}

type SingleProps = {
  mode?: 'single';
  value: string | null;
  onChange: (date: string | null) => void;
};

type RangeProps = {
  mode: 'range';
  value: DaySheetlyDateRange;
  onChange: (range: DaySheetlyDateRange) => void;
};

export type DaySheetlyDateProps = (SingleProps | RangeProps) & {
  label?: string;
  helperText?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  /** ISO yyyy-MM-dd. Inclusive lower bound. */
  minDate?: string;
  /** ISO yyyy-MM-dd. Inclusive upper bound. */
  maxDate?: string;
  /** Allow clearing via an "X" button on the trigger. Defaults to true. */
  clearable?: boolean;
  /** Value restored by the footer Reset action. */
  resetValue?: string | null | DaySheetlyDateRange;
  /** Render footer actions for resetting and applying the current selection. */
  showActions?: boolean;
  /** Control the popover from a parent when it needs to coordinate another surface. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Render the popover into a custom container, such as a mobile sheet. */
  popoverContainer?: HTMLElement | null;
  testId?: string;
  className?: string;
};

/**
 * DaySheetlyDate is the canonical date picker. It supports both a single
 * date (e.g. `birthdate`) and a range (e.g. filtering by date interval).
 *
 * Before reaching for `Popover` + `Calendar` directly, search the codebase
 * for `DaySheetlyDate` — this component is the canonical wrapper and already
 * handles `parseLocalDate` / `formatLocalDate` so dates stay timezone-safe.
 */
function DaySheetlyDate(props: DaySheetlyDateProps) {
  const {
    label,
    helperText,
    error,
    placeholder,
    disabled,
    minDate,
    maxDate,
    clearable = true,
    showActions = false,
    open,
    onOpenChange,
    popoverContainer,
    testId,
    className,
  } = props;

  const generatedId = React.useId();
  const fieldId = `${testId ?? generatedId}-field`;
  const helperId = helperText || error ? `${fieldId}-helper` : undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const isOpen = open ?? uncontrolledOpen;

  const isRange = props.mode === 'range';

  const triggerLabel = isRange
    ? formatRangeLabel(props.value, placeholder ?? 'Select date range')
    : formatSingleLabel(props.value, placeholder ?? 'Pick a date');

  const clearValue = () => {
    if (isRange) {
      props.onChange({ from: null, to: null });
    } else {
      props.onChange(null);
    }
  };

  const canReset = isRange
    ? props.resetValue !== null && typeof props.resetValue === 'object'
    : props.resetValue !== undefined;

  const resetSelection = () => {
    if (isRange) {
      if (props.resetValue === null || typeof props.resetValue !== 'object') return;
      props.onChange({
        from: props.resetValue.from ?? null,
        to: props.resetValue.to ?? null,
      });
      return;
    }

    if (props.resetValue === null || typeof props.resetValue === 'string') {
      props.onChange(props.resetValue);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    clearValue();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (open === undefined) {
      setUncontrolledOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const min = minDate ? parseLocalDate(minDate) : undefined;
  const max = maxDate ? parseLocalDate(maxDate) : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <Label
          htmlFor={fieldId}
          className={cn(disabled && 'opacity-70')}
        >
          {label}
        </Label>
      )}
      <div aria-describedby={helperId} aria-invalid={error ? 'true' : 'false'}>
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              id={fieldId}
              data-testid={testId ? `${testId}-trigger` : undefined}
              className={cn(
                'w-full justify-start rounded-xl text-left font-normal',
                !hasValue(props) && 'text-muted-foreground',
                error && 'border-destructive',
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              <span className="flex-1 truncate">{triggerLabel}</span>
              {clearable && hasValue(props) && !disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label="Clear date"
                  data-testid={testId ? `${testId}-clear` : undefined}
                  className="ml-2 rounded-sm p-0.5 hover:bg-accent"
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleClear(e as unknown as React.MouseEvent);
                    }
                  }}
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="center"
            sideOffset={8}
            collisionPadding={8}
            container={popoverContainer}
            aria-label={isRange ? 'Select date range' : `${label ?? 'Date'} picker`}
            data-testid={testId ? `${testId}-popover` : undefined}
            data-centered="true"
            style={{ zIndex: 400 }}
            className="max-h-[calc(100dvh-1rem)] w-[min(24rem,calc(100vw-2rem))] overflow-y-auto rounded-2xl p-0 shadow-xl"
          >
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="flex min-w-0 items-center gap-2 text-sm font-semibold">
                <CalendarIcon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span className="truncate">{isRange ? 'Select date range' : label ?? 'Select date'}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Close"
                className="h-8 w-8 shrink-0 rounded-full"
                onClick={() => handleOpenChange(false)}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>

            <div
              data-testid={testId ? `${testId}-calendar` : undefined}
              className="px-4 pb-3 pt-2"
            >
              {isRange ? (
                <Calendar
                  mode="range"
                  defaultMonth={
                    props.value.from
                      ? parseLocalDate(props.value.from)
                      : props.value.to
                        ? parseLocalDate(props.value.to)
                        : undefined
                  }
                  selected={
                    props.value.from || props.value.to
                      ? {
                          from: props.value.from ? parseLocalDate(props.value.from) : undefined,
                          to: props.value.to ? parseLocalDate(props.value.to) : undefined,
                        }
                      : undefined
                  }
                  onSelect={(range: DateRange | undefined) => {
                    props.onChange({
                      from: range?.from ? formatLocalDate(range.from) : null,
                      to: range?.to ? formatLocalDate(range.to) : null,
                    });
                  }}
                  disabled={[
                    ...(min ? [{ before: min }] : []),
                    ...(max ? [{ after: max }] : []),
                  ]}
                  className="w-full p-0"
                  classNames={calendarClassNames(true)}
                />
              ) : (
                <Calendar
                  mode="single"
                  defaultMonth={props.value ? parseLocalDate(props.value) : undefined}
                  selected={props.value ? parseLocalDate(props.value) : undefined}
                  onSelect={(date) => props.onChange(date ? formatLocalDate(date) : null)}
                  disabled={[
                    ...(min ? [{ before: min }] : []),
                    ...(max ? [{ after: max }] : []),
                  ]}
                  className="w-full p-0"
                  classNames={calendarClassNames(false)}
                />
              )}
            </div>

            {showActions && (
              <div
                data-testid={testId ? `${testId}-actions` : undefined}
                className="flex flex-row items-center gap-3 border-t px-4 py-3 [&>button]:min-w-0 [&>button]:flex-1"
              >
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 rounded-xl"
                  disabled={!canReset}
                  data-testid={testId ? `${testId}-reset-action` : undefined}
                  onClick={resetSelection}
                >
                  Reset
                </Button>
                <Button
                  type="button"
                  className="flex-1 rounded-xl"
                  data-testid={testId ? `${testId}-apply` : undefined}
                  onClick={() => handleOpenChange(false)}
                >
                  Apply
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
      {(helperText || error) && (
        <p
          id={helperId}
          className={cn(
            'text-xs',
            error ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {error ?? helperText}
        </p>
      )}
    </div>
  );
}

function calendarClassNames(isRange: boolean) {
  return {
    months: 'w-full',
    month: 'w-full space-y-3',
    month_caption: 'relative flex items-center justify-center pt-0',
    caption_label: 'text-base font-semibold',
    nav: 'absolute inset-x-0 top-0 flex w-full justify-between px-0',
    button_previous: 'h-8 w-8 rounded-full border-0 bg-transparent p-0 shadow-none hover:bg-accent',
    button_next: 'h-8 w-8 rounded-full border-0 bg-transparent p-0 shadow-none hover:bg-accent',
    month_grid: 'w-full border-collapse',
    weekdays: 'grid grid-cols-7',
    weekday: 'w-full text-center text-xs font-medium text-muted-foreground',
    weeks: 'space-y-1',
    week: 'grid grid-cols-7',
    day: 'relative w-full p-0 text-center',
    day_button: 'rdp-day_button mx-auto h-9 w-9 rounded-full p-0 font-normal transition-colors hover:bg-accent hover:text-accent-foreground',
    selected: isRange
      ? 'text-foreground'
      : 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
    today: 'border-2 border-primary bg-transparent text-primary',
    outside: 'text-muted-foreground/50',
    disabled: 'text-muted-foreground/50',
    range_start: 'rounded-l-full bg-primary text-primary-foreground',
    range_middle: 'rounded-none bg-primary/10 text-foreground',
    range_end: 'rounded-r-full bg-primary text-primary-foreground',
  };
}

function hasValue(props: DaySheetlyDateProps): boolean {
  if (props.mode === 'range') {
    return props.value.from !== null || props.value.to !== null;
  }
  return props.value !== null;
}

function formatSingleLabel(value: string | null, placeholder: string): string {
  if (!value) return placeholder;
  try {
    return format(parseLocalDate(value), 'PPP');
  } catch {
    return placeholder;
  }
}

function formatRangeLabel(range: DaySheetlyDateRange, placeholder: string): string {
  if (!range.from && !range.to) return placeholder;
  const from = range.from ? parseLocalDate(range.from) : null;
  const to = range.to ? parseLocalDate(range.to) : null;

  if (from && to) {
    const sameYear = from.getFullYear() === to.getFullYear();
    const sameMonth = sameYear && from.getMonth() === to.getMonth();
    if (sameMonth) {
      return `${format(from, 'MMM d')} - ${format(to, 'd, yyyy')}`;
    }
    if (sameYear) {
      return `${format(from, 'MMM d')} - ${format(to, 'MMM d, yyyy')}`;
    }
    return `${format(from, 'MMM d, yyyy')} - ${format(to, 'MMM d, yyyy')}`;
  }
  if (from) return `${format(from, 'PPP')} - ?`;
  if (to) return `? - ${format(to, 'PPP')}`;
  return placeholder;
}

DaySheetlyDate.displayName = 'DaySheetlyDate';

export { DaySheetlyDate };
